import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search, MapPin, Clock } from "lucide-react";
import { ALL_LOCATIONS, type Location } from "@/data/locations";
import { displayLocationTitle } from "@/lib/prayer/copy";
import type { PrayerVariant } from "@/lib/seo/prayer-schema";
import { cn } from "@/lib/utils";
import { pushHistory, setDefaultLocation, getHistory, type PrayerLink } from "@/lib/prayer/favorites";

function shortPath(variant: PrayerVariant, loc: Location): string {
  const base = variant === "buka-puasa" ? "/jadwal-buka-puasa" : `/jadwal-${variant}`;
  if (loc.kind === "kecamatan") return `${base}/${loc.parentSlug}/${loc.slug}`;
  return `${base}/${loc.slug}`;
}

const KIND_LABEL: Record<Location["kind"], string> = {
  provinsi: "Provinsi",
  kota: "Kota/Kab.",
  kecamatan: "Kecamatan",
};

/**
 * Always-visible search bar for jumping between locations on a schedule page.
 * Lightweight — no popover wrapper, just an input + dropdown.
 */
export function LocationQuickSearch({
  variant,
  placeholder = "Cari kota atau kecamatan...",
  className,
}: {
  variant: PrayerVariant;
  placeholder?: string;
  className?: string;
}) {
  const variantLabel =
    variant === "sholat" ? "jadwal sholat" : variant === "imsak" ? "jadwal imsakiyah" : "jadwal buka puasa";
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<PrayerLink[]>([]);
  const blurTimer = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setRecent(getHistory().filter((h) => h.variant === variant).slice(0, 5));
    const onStore = () => setRecent(getHistory().filter((h) => h.variant === variant).slice(0, 5));
    window.addEventListener("prayer:store", onStore);
    return () => window.removeEventListener("prayer:store", onStore);
  }, [variant]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const scored = ALL_LOCATIONS
      .map((l) => {
        const name = l.name.toLowerCase();
        const display = displayLocationTitle(l).toLowerCase();
        let score = 0;
        if (name === needle) score = 100;
        else if (name.startsWith(needle)) score = 80;
        else if (display.includes(needle)) score = 50;
        else if (name.includes(needle)) score = 30;
        // bias toward kecamatan/kota over province for typical search intent
        if (l.kind === "kecamatan") score += 2;
        if (l.kind === "kota") score += 1;
        return { l, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
    return scored.map((x) => x.l);
  }, [q]);

  // grouped matches by kind
  const grouped = useMemo(() => {
    const groups: Record<Location["kind"], Location[]> = { provinsi: [], kota: [], kecamatan: [] };
    for (const m of matches) groups[m.kind].push(m);
    return groups;
  }, [matches]);

  // reset active when query changes
  useEffect(() => { setActive(0); }, [q]);

  function go(loc: Location) {
    const link: PrayerLink = {
      variant,
      slug: loc.kind === "kecamatan" ? (loc.parentSlug ?? loc.slug) : loc.slug,
      kecSlug: loc.kind === "kecamatan" ? loc.slug : undefined,
      name: displayLocationTitle(loc),
      href: shortPath(variant, loc),
    };
    pushHistory(link);
    setDefaultLocation(link);
    setQ("");
    setFocus(false);
    navigate({ to: shortPath(variant, loc) as never });
  }

  const showRecent = focus && q.trim() === "" && recent.length > 0;
  const showResults = focus && matches.length > 0;

  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => {
          if (blurTimer.current) window.clearTimeout(blurTimer.current);
          setFocus(true);
        }}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setFocus(false), 150);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, Math.max(0, matches.length - 1)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === "Enter" && matches[active]) {
            e.preventDefault();
            go(matches[active]);
          } else if (e.key === "Escape") {
            setFocus(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={placeholder}
        aria-label={`Cari lokasi ${variantLabel}`}
        aria-autocomplete="list"
        aria-expanded={showResults || showRecent}
        className="w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm focus:border-prayer focus:outline-none focus:ring-1 focus:ring-prayer/30"
      />
      {showRecent && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-auto rounded-lg border border-border bg-card shadow-lg"
        >
          <li className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            Terakhir dipilih
          </li>
          {recent.map((r) => (
            <li key={`${r.slug}-${r.kecSlug ?? ""}`}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => navigate({ to: r.href as never })}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-prayer/10"
              >
                <Clock className="h-3.5 w-3.5 text-prayer" />
                <span className="flex-1 truncate">{r.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {showResults && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-auto rounded-lg border border-border bg-card shadow-lg"
        >
          {(["kecamatan", "kota", "provinsi"] as const).flatMap((kind) => {
            const items = grouped[kind];
            if (items.length === 0) return [];
            return [
              <li key={`h-${kind}`} className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                {KIND_LABEL[kind]}
              </li>,
              ...items.map((l) => {
                const idx = matches.indexOf(l);
                return (
                  <li key={`${l.kind}-${l.slug}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => go(l)}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-prayer/10",
                        idx === active && "bg-prayer/10",
                      )}
                    >
                      <MapPin className="h-3.5 w-3.5 text-prayer" />
                      <span className="flex-1 truncate">{displayLocationTitle(l)}</span>
                    </button>
                  </li>
                );
              }),
            ];
          })}
        </ul>
      )}
    </div>
  );
}
